import { useState } from 'react';
import { useStore } from '../store/useStore';
import { exportAllDocuments, deleteAccountAndCloudData } from '../lib/account';
import { t } from '../i18n';
import { Modal } from './Modal';

/**
 * Account settings: export the whole library and (when signed in) delete the
 * account together with its cloud data. Local documents survive a deletion as
 * local-only copies.
 */
export function AccountDialog() {
  useStore((s) => s.locale); // re-render on language change
  const open = useStore((s) => s.accountDialogOpen);
  const setOpen = useStore((s) => s.setAccountDialogOpen);
  const user = useStore((s) => s.user);
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  if (!open) return null;

  const close = () => {
    setOpen(false);
    setNote(null);
    setConfirmDelete(false);
  };

  const doExport = async () => {
    setBusy('export');
    try {
      const n = await exportAllDocuments();
      setNote(t('account.exported', { n }));
    } finally {
      setBusy(null);
    }
  };

  const doDelete = async () => {
    setBusy('delete');
    setNote(null);
    try {
      await deleteAccountAndCloudData();
      setNote(t('account.deleted'));
      setConfirmDelete(false);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setNote(/recent-login/i.test(msg) ? t('account.reauth') : t('account.deleteFail', { msg }));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Modal onClose={close} title={t('account.title')}>
      <section className="mb-account-section">
        <h3>{t('account.exportH')}</h3>
        <p className="mb-modal-note">{t('account.exportBody')}</p>
        <button className="mb-btn" disabled={busy === 'export'} onClick={() => void doExport()}>
          {busy === 'export' ? t('account.exporting') : t('account.exportBtn')}
        </button>
      </section>

      {user && (
        <section className="mb-account-section mb-account-danger">
          <h3>{t('account.deleteH')}</h3>
          <p className="mb-modal-note">{t('account.deleteBody')}</p>
          {!confirmDelete ? (
            <button className="mb-btn mb-btn-danger" onClick={() => setConfirmDelete(true)}>
              {t('account.deleteBtn')}
            </button>
          ) : (
            <div className="mb-modal-actions" style={{ justifyContent: 'flex-start' }}>
              <button
                className="mb-btn mb-btn-danger"
                disabled={busy === 'delete'}
                onClick={() => void doDelete()}
              >
                {busy === 'delete' ? t('account.deleting') : t('account.deleteConfirm')}
              </button>
              <button className="mb-btn" onClick={() => setConfirmDelete(false)}>
                {t('common.cancel')}
              </button>
            </div>
          )}
        </section>
      )}

      {note && <p className="mb-account-msg">{note}</p>}

      <div className="mb-modal-actions">
        <button className="mb-btn mb-btn-primary" onClick={close}>
          {t('common.done')}
        </button>
      </div>
    </Modal>
  );
}
